import { MutationObserverOptions, useMutation, useQuery, useQueryClient, UseQueryOptions } from '@tanstack/vue-query';
import { AxiosError } from 'axios';
import { type MaybeRefOrGetter, toValue } from 'vue';

import { PaginatedResponse } from '@/models/api';
import { BillingInvoice, BillingPaymentMethod, GetBillingInvoicesArgs } from '@/models/billing';
import { CompanyBillingRequestData, CompanyBillingResponseData } from '@/services/api/models/settings';
import {
  getBillingInvoicesV2,
  getBillingPaymentMethods,
  patchCompanyBilling,
  payInvoiceV2,
  PayInvoiceV2RequestArgs,
} from '@/services/api/settings';

const settingsBillingQueryKeys = {
  invoices: () => ['settings', 'billing', 'invoices'] as const,
  paymentMethods: () => ['settings', 'billing', 'paymentMethods'] as const,
};

type UseCompanyBillingPatchOptions = MutationObserverOptions<
  CompanyBillingResponseData,
  AxiosError,
  CompanyBillingRequestData
>;

export function useCompanyBillingPatch(options?: UseCompanyBillingPatchOptions) {
  return useMutation({
    mutationFn: patchCompanyBilling,
    ...options,
  });
}

type UseBillingInvoicePaymentMutationOptions = MutationObserverOptions<void, AxiosError, PayInvoiceV2RequestArgs>;

export function useBillingInvoicePaymentMutation(options?: UseBillingInvoicePaymentMutationOptions) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: payInvoiceV2,
    ...options,
    onSuccess(...args) {
      queryClient.refetchQueries({ queryKey: settingsBillingQueryKeys.invoices() });
      options?.onSuccess?.(...args);
    },
  });
}

type CompanyBillingInvoicesResponse = PaginatedResponse<BillingInvoice>;

type UseBillingInvoicesQueryOptions = Partial<UseQueryOptions<CompanyBillingInvoicesResponse>>;

type UseBillingInvoicesQueryParams = {
  filters?: MaybeRefOrGetter<GetBillingInvoicesArgs>;
  options?: UseBillingInvoicesQueryOptions;
};

export const useBillingInvoicesQuery = ({ filters, ...options }: UseBillingInvoicesQueryParams) => {
  return useQuery({
    queryKey: settingsBillingQueryKeys.invoices(),
    queryFn: () => getBillingInvoicesV2(toValue(filters)),
    ...options,
  });
};

type UseBillingPaymentMethodsQueryOptions = Partial<UseQueryOptions<BillingPaymentMethod[]>>;

export const useBillingPaymentMethodsQuery = (options?: UseBillingPaymentMethodsQueryOptions) => {
  return useQuery({
    queryKey: settingsBillingQueryKeys.paymentMethods(),
    queryFn: getBillingPaymentMethods,
    ...options,
  });
};
